import {
  MutationFunction,
  MutationKey,
  QueryFunction,
  QueryKey,
} from '@tanstack/react-query';

export enum EFeedbackTypes {
  USEFUL = 'USEFUL',
  NOT_USEFUL = 'NOT_USEFUL',
}

export enum EFeedbackEvents {
  UPVOTE = 'UPVOTE',
  DOWNVOTE = 'DOWNVOTE',
}

export enum ECardStates {
  LOADING = 'LOADING',
  ERROR = 'ERROR',
  STREAMING = 'STREAMING',
}

export interface IActionTypes {
  type?: EActionTypes;
  value?: string;
}

export interface ICTATypes {
  text?: string;
  variant: ECTAVariants;
  action?: IActionTypes;
}

export enum ECardTypes {
  SINGLE_SELECT_TYPE = 'SINGLE_SELECT_TYPE',
  MULTI_SELECT_TYPE = 'MULTI_SELECT_TYPE',
  SLIDER_SELECT_TYPE = 'SLIDER_SELECT_TYPE',
  TEXT_SELECT_TYPE = 'TEXT_SELECT_TYPE',
  LEARN_MORE_CARD_TYPE = 'learn_more_card_type',
  CAROUSEL_CARD_TYPE = 'CAROUSEL_TYPE',
  ERROR_CARD_TYPE = 'ERROR_TYPE',
  FALLBACK_CARD_TYPE = 'FALLBACK_TYPE',
  LOADING_CARD_TYPE = 'LOADING_TYPE',
}

export enum ECardVariants {
  GRID = 'GRID',
}

export enum ECTAVariants {
  OUTLINED = 'OUTLINED',
  CONTAINED = 'CONTAINED',
}

export enum ECTATypes {
  primary = 'primary',
  secondary = 'secondary',
}

export enum EEventTypes {
  ERROR = 'ERROR',
  WARNING = 'WARNING',
  INFO = 'INFO',
  /** Marks the doctor-card container holding the "we recommend because" copy.
   * Matched by variant rather than position, so the backend can add it without
   * shifting the positionally-destructured containers around it. */
  RECOMMENDATION = 'RECOMMENDATION',
}

export interface IOptionType {
  label?: string;
  icon?: string;
  altText?: string;
  event?: EEventTypes;
  image?: string;
  valueLimits?: {
    max: number;
    min: number;
    default: number;
    step: number;
  };
  valueDescriptions?: ISliderOptionType[];
  body?: ICarouselComponentType[];
  footer?: ICarouselComponentType[];
  settings?: ISettingType;
}

export interface IBaseQuestionType {
  title?: string;
  subtitle?: string;
  cardType: ECardTypes;
  /** Artwork URL. On a fallback card its presence selects the illustrated
   * centred design over the peach medical alert. */
  image?: string;
  primaryCTA?: ICTATypes;
  secondaryCTA?: ICTATypes;
  settings?: {
    showFeedback?: boolean;
    nextLoader?: string;
    extraInfo?: { reasonForConsultation: string; patientRelation: string };
    /**
     * Marks the crisis card. FALLBACK_TYPE covers three designs — the medical
     * alert, the "not qualified to prescribe" illustration and the generic
     * error — and they are otherwise indistinguishable in the payload apart
     * from `image`. Set by the backend; drives the emergencyDetected event in
     * QuestionScreen. Nothing renders from it.
     */
    isEmergency?: boolean;
    allowCustomInput?: boolean;
    customInputPlaceholder?: string | null;
  };
  variant?: ECardVariants | '';
  options?: IOptionType[];
}

export interface IAnsweredQuestionTypeBase {
  answer?: string | number | boolean;
}

export type IAnsweredQuestionType = IBaseQuestionType &
  IAnsweredQuestionTypeBase;

export type IQuestionType = IBaseQuestionType;

export interface IResponseQuestionType {
  conversation_id: string;
  response: IAnsweredQuestionType | IQuestionType;
  answer?: string;
  uniqueId?: string;
}

export enum EGradientVariants {
  primary = 'primary',
  secondary = 'secondary',
  accent = 'accent',
  custom = 'custom',
  focused = 'focused-card',
}

export enum EMessageType {
  TOKEN = 'TOKEN',
  READY = 'READY',
  // CB = CLAIMS_BOT
  CB_CLAIM_DETAILS = 'CB_CLAIM_DETAILS',
  CB_INITIAL_PROMPT = 'CB_INITIAL_PROMPT',
  CB_CLEAR_CHAT = 'CB_CLEAR_CHAT',
  CLOSE_IFRAME = 'CLOSE_IFRAME',
  // NB = NativeBridge
  NB_CALLBACK = 'NB_CALLBACK',
  CLOSE_FPM_MODAL = 'CLOSE_FPM_MODAL',
  OPEN_ARTICLE = 'OPEN_ARTICLE',
}

export interface IMessagePayload {
  type: EMessageType;
  data: Array<EMessageType> | string | object | boolean;
}

export enum EEnrollmentStatus {
  null = 'null',
  INCOMPLETE = 'INCOMPLETE',
  ENROLLED = 'ENROLLED',
  COMPLETE = 'COMPLETE',
  UNSET = 'UNSET',
}
export interface IFeatureFlag {
  name: string;
  enabled: boolean;
}
export interface IUserConfig {
  id: string;
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isPhoneVerified: boolean;
  organisation: {
    id: string;
    name: string;
  };
  enrollmentStatus: EEnrollmentStatus;
  gender: string;
  dateOfBirth: {
    date: number;
    month: string;
    monthNumber: number;
    year: number;
  };
  eid: string;
  featureFlags: Array<IFeatureFlag>;
  source: string;
  care: {
    planId: string;
    planType: string;
    active: boolean;
    benefitGrade: string;
  };
  admin: boolean;
  super: boolean;
  statusInternal?: string;
  defaultRoute: string;
  enrollmentUrl: string;
  inviteCreatedAt: string;
  personalEmail: string;
  isMidTermAdditionEnabled: boolean;
  cultPromoOpenTillTime?: number;
  showCultPromo?: boolean;
}

export type IMutationQuery<T> = {
  mutationKey: MutationKey;
  mutationFn: MutationFunction<T>;
};

export type IQuery<T> = {
  queryKey: QueryKey;
  queryFn: QueryFunction<T>;
  cacheTime?: number;
  staleTime?: number;
};

export interface IAccountDetail {
  memberId: string;
  accountSwitcherOrganisationStatus?: string;
  currentLoggedIn: boolean;
  organizationName: string;
  accountValidTill: string;
  contextualInfo: string;
  isDependent: boolean;
  authCode: string;
  loginType: 'MEMBER' | 'IAM';
}
/**
 * For Slider component
 */

export interface ISliderOptionType {
  label: number;
  title: string;
  subtitle: string;
  valueColor?: string;
}

export interface ISliderSelectCardProps {
  valueLimits: {
    max: number;
    min: number;
    default: number;
    step: number;
  };
  valueDescriptions: ISliderOptionType[];
  primaryCTA?: ICTATypes;
  secondaryCTA?: ICTATypes;
  answer?: string;
  isAnswered?: boolean;
}

/**
 * For Carousel component's slides
 */

export enum EComponentTypes {
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
  CONTAINER = 'CONTAINER',
  ICON = 'ICON',
  DIVIDER = 'DIVIDER',
  BUTTON = 'BUTTON',
}

export interface ILayoutType {
  direction?: 'column' | 'row';
  alignItems?: 'center' | 'start' | 'end' | 'stretch' | 'baseline';
  justifyContent?:
    | 'center'
    | 'start'
    | 'end'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  gap?: number;
}

export enum EContentTypeTypes {
  LABEL = 'LABEL',
  ICON = 'ICON',
  TITLE = 'TITLE',
  SUBTITLE = 'SUBTITLE',
  ELLIPSE = 'ELLIPSE',
}

export enum EActionTypes {
  DEEPLINK = 'DEEPLINK',
  RETRY = 'RETRY',
  CALL = 'CALL',
}

export interface IContentType {
  items?: ICarouselComponentType[];
  icon?: string;
  altText?: string;
  type: EEventTypes;
  text: string;
  url: string;
  tooltip: string;
  format: ETextFormatTypes;
  action: IActionTypes;
}

export interface ISettingType {
  action?: IActionTypes;
}

export interface ICarouselComponentType {
  componentType: EComponentTypes;
  content: IContentType;
  settings: ISettingType;
  variant?: EEventTypes;
}

export interface ICarouselCardType {
  body: ICarouselComponentType[];
  footer: ICarouselComponentType[];
  settings: ISettingType;
}

export interface IDoctorSuggestionsType {
  options: IOptionType[];
  extraInfo?: { reasonForConsultation: string; patientRelation: string };
}

export enum ETextFormatTypes {
  STRIKETHROUGH = 'STRIKETHROUGH',
  UNDERLINE = 'UNDERLINE',
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  HIGHLIGHT = 'HIGHLIGHT',
  HYPERLINK = 'HYPERLINK',
  BULLET = 'BULLET',
}

export enum ERouteNames {
  MENTAL_HEALTH_EXPERT = 'MENTAL_HEALTH_EXPERT',
}

export enum ERoutePaths {
  MATCH_SPECIALIST = 'match-specialist',
  MATCH_THERAPIST = 'match-therapist',
  MATCH_THERAPIST_V2 = 'match-therapist-v2',
  QUESTIONS = 'questions',
}
